import React, { useState } from 'react';
import { X } from 'lucide-react';
import styles from './AddSeries.module.css';
import { useCreateSeries } from '../../../hooks/admin/useAdminSeries';
import { useCategories } from '../../../hooks/categories/useCategories';
import useAuthStore from '../../../store/authStore';

const AddSeries = () => {
  const { user } = useAuthStore(); // Get the current user from auth store
  const { createSeries, isLoading } = useCreateSeries();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    series_type: 'Ebook', // Default to Ebook for all users
    audio_type: '',
    visibility: 'public',
    is_published: true,
    cover_image: null,
    author: '',
    language: 'english',
    seo_title: '',
    seo_description: '',
    content_rating: 'all',
    prerequisites: '',
    target_audience: '',
    meta_keywords: '',
    price: '0',
    is_featured: false,
    is_premium: false,
    status: 'draft',
    category_id: ''
  });

  // Use the useCategories hook to fetch categories based on series_type
  const { categories, isLoading: categoriesLoading, error: categoriesError } = useCategories(formData.series_type);

  const [previewImage, setPreviewImage] = useState('');

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Reset category_id when series_type changes
    if (name === 'series_type') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        category_id: '', // Reset category when series type changes
        audio_type: value === 'Audio' ? 'Audio Series' : '' // Reset audio_type based on series_type
      }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        alert('File size should be less than 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
        setFormData(prev => ({ ...prev, cover_image: file }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setPreviewImage('');
    setFormData(prev => ({ ...prev, cover_image: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createSeries(formData);
      // Navigate back to the previous page
    } catch (error) {
      // Error is handled by the hook
    }
  };

  // Determine available series types based on user role
  const getSeriesTypeOptions = () => {
    // Only super_admin can see all options, regular admin only sees Ebook
    if (user?.role === 'super_admin') {
      return (
        <>
          <option value="Ebook">Ebook</option>
          <option value="Videos">Video</option>
          <option value="Audio">Audio</option>
          <option value="Shorts">Shorts</option>
        </>
      );
    } else {
      // Regular admin only sees Ebook
      return <option value="Ebook">Ebook</option>;
    }
  };

  return (
    <div className={styles.container}>
      <form className={styles.form} onSubmit={handleSubmit}>
        <h1 className={styles.title}>Create New Series</h1>
        
        {/* Cover Image Upload */}
        <div className={styles.imageUpload}>
          {previewImage ? (
            <div className={styles.previewContainer}>
              <img 
                src={previewImage} 
                alt="Preview" 
                className={styles.preview}
              />
              <button 
                type="button" 
                onClick={removeImage}
                className={styles.removeButton}
              >
                <X size={24} />
              </button>
            </div>
          ) : (
            <div className={styles.uploadPlaceholder}>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className={styles.fileInput}
              />
              <span>Click to upload cover image</span>
            </div>
          )}
        </div>

        {/* Basic Information */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Basic Information</h2>
          
          <div className={styles.inputGroup}>
            <label htmlFor="title">Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
              className={styles.input}
            />
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="author">Author *</label>
            <input
              type="text"
              id="author"
              name="author"
              value={formData.author}
              onChange={handleInputChange}
              required
              className={styles.input}
            />
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="description">Description *</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="4"
              required
              className={styles.textarea}
            />
          </div>
        </div>

        {/* Content Details */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Content Details</h2>

          <div className={styles.inputGroup}>
            <label htmlFor="series_type">Series Type *</label>
            <select
              id="series_type"
              name="series_type"
              value={formData.series_type}
              onChange={handleInputChange}
              required
              className={styles.select}
              disabled={user?.role !== 'super_admin'} // Disable for regular admin
            >
              {getSeriesTypeOptions()}
            </select>
          </div>
          
          {formData.series_type === 'Audio' && (
            <div className={styles.inputGroup}>
              <label htmlFor="audio_type">Audio Type *</label>
              <select
                id="audio_type"
                name="audio_type"
                value={formData.audio_type}
                onChange={handleInputChange}
                required
                className={styles.select}
              >
                <option value="">Select Audio Type</option>
                <option value="Audio Series">Audio Series</option>
                <option value="Audio Book">Audio Book</option>
              </select>
            </div>
          )}

          {/* Category Dropdown - Changes based on series_type */}
          <div className={styles.inputGroup}>
            <label htmlFor="category_id">Category *</label>
            <select
              id="category_id"
              name="category_id"
              value={formData.category_id}
              onChange={handleInputChange}
              required
              className={styles.select}
              disabled={categoriesLoading || !categories?.length}
            >
              <option value="">Select Category</option>
              {categories?.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
            {categoriesLoading && <span className={styles.helperText}>Loading categories...</span>}
            {categoriesError && <span className={styles.errorText}>{categoriesError}</span>}
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="language">Language *</label>
            <select
              id="language"
              name="language"
              value={formData.language}
              onChange={handleInputChange}
              required
              className={styles.select}
            >
              <option value="english">English</option>
              <option value="hindi">Hindi</option>
              <option value="garhwali">Garhwali</option>
              <option value="kumaoni">Kumaoni</option>
              <option value="italian">Italian</option>
            </select>
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="content_rating">Content Rating *</label>
            <select
              id="content_rating"
              name="content_rating"
              value={formData.content_rating}
              onChange={handleInputChange}
              required
              className={styles.select}
            >
              <option value="all">All Ages</option>
              <option value="teen">Teen</option>
              <option value="mature">Mature</option>
            </select>
          </div>

        </div>

        {/* SEO Information */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>SEO Information</h2>

          <div className={styles.inputGroup}>
            <label htmlFor="seo_title">SEO Title</label>
            <input
              type="text"
              id="seo_title"
              name="seo_title"
              value={formData.seo_title}
              onChange={handleInputChange}
              className={styles.input}
            />
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="seo_description">SEO Description</label>
            <textarea
              id="seo_description"
              name="seo_description"
              value={formData.seo_description}
              onChange={handleInputChange}
              rows="3"
              className={styles.textarea}
            />
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="meta_keywords">Meta Keywords</label>
            <input
              type="text"
              id="meta_keywords"
              name="meta_keywords"
              value={formData.meta_keywords}
              onChange={handleInputChange}
              placeholder="Comma-separated keywords"
              className={styles.input}
            />
          </div>
        </div>

        {/* Publishing Options */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Publishing Options</h2>

          <div className={styles.inputGroup}>
            <label htmlFor="status">Status *</label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              required
              className={styles.select}
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="scheduled">Scheduled</option>
            </select>
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="visibility">Visibility *</label>
            <select
              id="visibility"
              name="visibility"
              value={formData.visibility}
              onChange={handleInputChange}
              required
              className={styles.select}
            >
              <option value="public">Public</option>
              <option value="private">Private</option>
              <option value="unlisted">Unlisted</option>
            </select>
          </div>

          <div className={styles.checkboxGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                name="is_published"
                checked={formData.is_published}
                onChange={handleInputChange}
                className={styles.checkbox}
              />
              Publish immediately
            </label>
          </div>

          <div className={styles.checkboxGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                name="is_featured"
                checked={formData.is_featured}
                onChange={handleInputChange}
                className={styles.checkbox}
              />
              Featured series
            </label>
          </div>

          <div className={styles.checkboxGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                name="is_premium"
                checked={formData.is_premium}
                onChange={handleInputChange}
                className={styles.checkbox}
              />
              Premium content
            </label>
          </div>
        </div>

        <button 
          type="submit" 
          className={styles.submitButton}
          disabled={isLoading}
        >
          {isLoading ? 'Creating...' : 'Create Series'}
        </button>
      </form>
    </div>
  );
};

export default AddSeries;